/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react'
import type { ComponentProps } from 'react'
import { forwardRef, useEffect, useRef, useState } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { Controller, useFormContext, useFormState } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { cn } from '../../../lib/utils'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../ui/field'
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover'

export type TagMultiSelectOption = {
  label: string
  value: string
  disabled?: boolean
}

/** When `close`, `onValueChange` runs when the dropdown closes (if the value changed), and immediately for clear / chip remove while the list is closed. Form state still updates on every change. */
export type TagMultiSelectOnValueChangeTrigger = 'change' | 'close'

export type TagMultiSelectFormFieldProps = {
  closeOnSelect?: boolean
  disabled?: boolean
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  label?: string
  name: string
  /** Fired according to `onValueChangeTrigger`. */
  onValueChange?: (value: string[]) => void
  /** `'change'` (default): call `onValueChange` on every update. `'close'`: call when the popup closes after a change from the list, or immediately for clear / chip remove while the popup is not open. */
  onValueChangeTrigger?: TagMultiSelectOnValueChangeTrigger
  options: TagMultiSelectOption[]
  placeholder?: string
  required?: boolean
  validation?: RegisterOptions
}

function tagArraysEqual(a: string[], b: string[]) {
  if (a.length !== b.length) {
    return false
  }
  return a.every((v, i) => v === b[i])
}

function normalizeFieldValue(fieldValue: unknown): string[] {
  if (Array.isArray(fieldValue)) {
    return fieldValue.filter((x): x is string => typeof x === 'string')
  }
  if (fieldValue && typeof fieldValue === 'object' && 'length' in (fieldValue as object)) {
    return (fieldValue as { value?: string }[]).map((x) => (typeof x === 'string' ? x : (x as { value: string }).value))
  }
  return []
}

export const TagMultiSelectFormField = forwardRef<HTMLDivElement, TagMultiSelectFormFieldProps>(
  (props, forwardedRef) => {
    const { control } = useFormContext()
    const { errors } = useFormState({ control, name: props.name })

    const {
      closeOnSelect = false,
      disabled,
      fieldProps,
      helperText,
      label,
      name,
      onValueChange,
      onValueChangeTrigger = 'change',
      options,
      placeholder = 'Select…',
      required,
      validation,
    } = props

    const error = errors[name]
    const invalid = !!error

    return (
      <div ref={forwardedRef}>
        <Field {...fieldProps} data-invalid={invalid}>
          {label !== undefined && (
            <FieldLabel className="flex items-center gap-1">
              {label}
              {required ? (
                <span aria-hidden className="text-destructive">
                  *
                </span>
              ) : null}
            </FieldLabel>
          )}
          <Controller
            control={control}
            name={name}
            render={({ field }) => {
              const value = normalizeFieldValue(field.value)

              return (
                <TagMultiSelectInner
                  closeOnSelect={closeOnSelect}
                  disabled={disabled}
                  invalid={invalid}
                  onFormValueChange={field.onChange}
                  onNotifyValueChange={onValueChange}
                  onValueChangeTrigger={onValueChangeTrigger}
                  options={options}
                  placeholder={placeholder}
                  value={value}
                />
              )
            }}
            rules={validation}
          />
          {helperText !== undefined ? <FieldDescription>{helperText}</FieldDescription> : null}
          {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
        </Field>
      </div>
    )
  }
)

TagMultiSelectFormField.displayName = 'TagMultiSelectFormField'

function TagMultiSelectInner({
  closeOnSelect,
  disabled,
  invalid,
  onFormValueChange,
  onNotifyValueChange,
  onValueChangeTrigger,
  options,
  placeholder,
  value,
}: {
  closeOnSelect: boolean
  disabled?: boolean
  invalid: boolean
  onFormValueChange: (value: string[]) => void
  onNotifyValueChange?: (value: string[]) => void
  onValueChangeTrigger: TagMultiSelectOnValueChangeTrigger
  options: TagMultiSelectOption[]
  placeholder: string
  value: string[]
}) {
  const { t } = useTranslation()
  const valueToLabel = new Map(options.map((o) => [o.value, o.label]))
  const [open, setOpen] = useState(false)
  const openRef = useRef(false)
  openRef.current = open

  const prevOpenRef = useRef(false)
  const valueWhenOpenedRef = useRef<string[]>([])

  const commit = (newValue: string[], opts?: { forceNotify?: boolean }) => {
    onFormValueChange(newValue)
    if (!onNotifyValueChange) {
      return
    }
    if (opts?.forceNotify || onValueChangeTrigger === 'change' || !openRef.current) {
      onNotifyValueChange(newValue)
    }
  }

  useEffect(() => {
    if (!open && prevOpenRef.current) {
      if (
        onValueChangeTrigger === 'close' &&
        onNotifyValueChange &&
        !tagArraysEqual(value, valueWhenOpenedRef.current)
      ) {
        onNotifyValueChange(value)
      }
    }
    if (open && !prevOpenRef.current) {
      valueWhenOpenedRef.current = [...value]
    }
    prevOpenRef.current = open
  }, [open, onNotifyValueChange, onValueChangeTrigger, value])

  const toggleValue = (optionValue: string, optionDisabled?: boolean) => {
    if (disabled || optionDisabled) {
      return
    }
    const exists = value.includes(optionValue)
    const next = exists ? value.filter((v) => v !== optionValue) : [...value, optionValue]
    commit(next)
    if (closeOnSelect) {
      setOpen(false)
    }
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-invalid={invalid}
        className={cn(
          'flex min-h-10 w-full flex-wrap items-center gap-1 rounded-lg border border-input bg-transparent px-2.5 py-1 text-left transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 dark:bg-input/30',
          invalid && 'border-destructive'
        )}
        disabled={disabled}
        nativeButton={false}
        render={<div />}
      >
        {value.length === 0 ? <span className="flex-1 text-muted-foreground">{placeholder}</span> : null}
        {value.map((tag) => (
          <Badge className="gap-1 py-3 pr-0.5" key={tag} variant="default">
            {valueToLabel.get(tag) ?? tag}
            <button
              aria-label={`Remove ${valueToLabel.get(tag) ?? tag}`}
              className="rounded-sm p-0.5 hover:bg-muted hover:text-muted-foreground"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation()
                if (disabled) {
                  return
                }
                const next = value.filter((v) => v !== tag)
                commit(next, { forceNotify: true })
              }}
              type="button"
            >
              <XIcon className="size-3.5" />
            </button>
          </Badge>
        ))}
        <span className="ml-auto inline-flex shrink-0 items-center gap-0.5 text-muted-foreground">
          {value.length > 0 ? (
            <button
              aria-label={t('Clear all selections')}
              className="rounded-sm p-0.5 text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation()
                if (disabled) {
                  return
                }
                setOpen(false)
                commit([], { forceNotify: true })
              }}
              type="button"
            >
              <XIcon className="size-4" />
            </button>
          ) : null}
          <ChevronDownIcon className="size-4" />
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--anchor-width)] min-w-[var(--anchor-width)] p-1">
        <div className="max-h-60 overflow-y-auto py-1" role="listbox">
          {options.length === 0 ? (
            <p className="px-2 py-2 text-center text-muted-foreground">No options</p>
          ) : (
            options.map((opt) => {
              const selected = value.includes(opt.value)
              return (
                <button
                  aria-selected={selected}
                  className={cn(
                    'flex w-full cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-left outline-none hover:bg-accent hover:text-accent-foreground',
                    opt.disabled && 'pointer-events-none opacity-50'
                  )}
                  disabled={opt.disabled}
                  key={opt.value}
                  onClick={() => toggleValue(opt.value, opt.disabled)}
                  role="option"
                  type="button"
                >
                  <span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
                    {selected ? <CheckIcon className="size-4" /> : null}
                  </span>
                  <span className="min-w-0 flex-1">{opt.label}</span>
                </button>
              )
            })
          )}
        </div>
        {value.length > 0 && (
          <div className="border-t px-1 pt-1">
            <Button
              className="h-8 w-full justify-start"
              disabled={disabled}
              onClick={() => {
                setOpen(false)
                commit([], { forceNotify: true })
              }}
              type="button"
              variant="ghost"
            >
              Clear selection
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
